<template>
  <div class="chronology-reply">
    <!-- REPLY ICON -->
    <i
      :class="{'rotate-180' : !isReplyFromOutsideUser}"
      class="ddi ddi-return chronology-reply__reply-icon"
    />

    <!-- USER AVATAR -->
    <div class="chronology-reply__avatar">
      <avatar :person="replyInfo.creator" :size="avatarSize" />
    </div>

    <!-- DATE  -->
    <div class="chronology-reply__date">
      {{ replyInfo.createdOn }}
      <span v-if="replyInfo.isEdited">&nbsp;&mdash;&nbsp;Edited</span>
    </div>  

    <!-- ACTION -->
    <div class="chronology-reply__action-text" v-html="replyInfo.action" />

    <!-- EDIT FORM -->
    <el-form
      v-if="isEditing"
      :disabled="isSubmitting"
      :model="{ editedIncomingReply }"
    >
      <el-form-item>
        <editor
          v-model="editedIncomingReply"
          :autofocus="true"
          :disabled="isSubmitting"
          :name="`reply-${replyInfo.id}`"
          placeholder="Edit incoming reply..."
        />
      </el-form-item>

      <div class="chronology-reply__form-actions">
        <dd-button
          :is-disabled="isSubmitting"
          type="text"
          color="grey"
          size="medium"
          @click="disableEditMode"
        >Cancel</dd-button>
        <dd-button
          :is-disabled="isSubmitting"
          :is-loading="isSubmitting"
          :color="ColorAssistant.MailboxColor"
          size="medium"
          @click="submit"
        >Save Changes</dd-button>
      </div>
    </el-form>
    <!-- CONTENT -->
    <div
      v-else
      class="user-inputed-rich-text"
      v-html="replyInfo.description"
    />

    <div v-if="hasOtherInfo" class="chronology-reply__other-info">
      <!-- ATTACHMENTS LIST -->
      <attachments-list :attachments="replyInfo.attachments" />
      <!-- EMAIL DELIVERY NOTES -->
      <email-delivery-notes
        :ccs="replyInfo.ccs"
        :auto-response-sent="replyInfo.autoResponseSent"
        :bounce-info="replyInfo.bounceInfo"
      />
    </div>

    <!-- MORE OPTIONS -->
    <div v-if="!isEditing && (showMoreOptions || $screen.isMobile($mq))" class="chronology-reply__more-options">
      <template v-if="$screen.isTabletOrDesktop($mq)">
        <!-- Conditions go on the tooltips: Element Plus warns when a trigger renders nothing -->
        <el-tooltip v-if="hasStylizedHtmlLink" placement="top">
          <i
            class="ddi ddi-email-read"
            title="View original email"
            @click="$emit('original-message-requested', replyInfo)"
          />
          <template #content>
            <div>
              View original email
            </div>
          </template>
        </el-tooltip>
        <el-tooltip v-if="isReplyFromOutsideUser && $permissions.isAdminOrOwner" placement="top">
          <i
            class="ddi ddi-edit"
            title="Edit this reply"
            @click="enableIncomingReplyEdit"
          />
          <template #content>
            <div>
              Edit this incoming reply
            </div>
          </template>
        </el-tooltip>
        <el-tooltip placement="top">
          <i
            class="ddi ddi-task-add"
            @click="initiateLinkedTask(replyInfo.id)"
          />
          <template #content>
            <div>
              Create linked task from this reply
            </div>
          </template>
        </el-tooltip>
      </template>
      <more-button-dropdown
        v-else
        :force-visibility="showMoreOptions"
        vertical-ellipsis
      >
        <template #options-list>
          <ul>
            <li
              v-if="hasStylizedHtmlLink"
              @click="handleViewOriginalEmail"
            >
              <i class="ddi ddi-email-read" />
              <span>View original email</span>
            </li>
            <li
              v-if="isReplyFromOutsideUser && $permissions.isAdminOrOwner"
              @click="enableIncomingReplyEdit"
            >
              <i class="ddi ddi-edit" />
              <span>Edit this incoming reply</span>
            </li>
            <li @click="initiateLinkedTask(replyInfo.id)">
              <i class="ddi ddi-task-add" />
              <span>Create linked task from this reply</span>
            </li>
          </ul>
        </template>
      </more-button-dropdown>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { IChronologyReply } from '@/types/data-objects/item-histories'
import ColorAssistant from '@/helpers/color-assistant'
import { useAppStore } from '@/modules/app/app.store'

export default defineComponent({
  name: 'ChronologyReply',

  props: {
    replyInfo: {
      type: Object as () => IChronologyReply,
      required: true,
    },
    isReplyFromOutsideUser: {
      type: Boolean,
      default: false,
    },
    rolledOverReply: {
      type: Boolean,
      default: false,
    },
    // Function used when submit edited reply
    submitHandler: {
      type: Function,
      required: true,
      default: (): any => {},
    },
  },

  emits: ['original-message-requested'],

  data() {
    return {
      ColorAssistant,
      isEditing: false,
      isSubmitting: false,
      showMoreOptions: false,
      editedIncomingReply: '',
    }
  },

  computed: {
    avatarSize(): number {
      return this.$screen.isDesktop(this.$mq) ? 40 : 24
    },
    hasStylizedHtmlLink(): boolean {
      return (
        this.replyInfo.stylizedHtmlVersionLink !== null &&
        this.replyInfo.stylizedHtmlVersionLink.trim() !== ''
      )
    },
    hasAttachments(): boolean {
      return this.replyInfo.attachments.length > 0
    },
    hasCCs(): boolean {
      return this.replyInfo.ccs !== null && this.replyInfo.ccs.length > 0
    },
    hasBounceInfo(): boolean {
      return this.replyInfo.bounceInfo !== null
    },
    hasOtherInfo(): boolean {
      return (
        this.hasAttachments ||
        this.hasCCs ||
        this.hasBounceInfo ||
        this.replyInfo.autoResponseSent
      )
    },
  },
  watch: {
    replyInfo: {
      immediate: true,
      handler(value) {
        this.editedIncomingReply = value.description
      },
    },
    rolledOverReply: {
      handler(value) {
        if (this.$screen.isTabletOrDesktop(this.$mq)) {
          this.showMoreOptions = value
        }
      },
    },
  },
  methods: {
    closeOptions() {
      this.showMoreOptions = true
      this.$nextTick(() => {
        this.showMoreOptions = false
      })
    },
    handleViewOriginalEmail() {
      this.closeOptions()
      this.$emit('original-message-requested', this.replyInfo)
    },
    initiateLinkedTask(id: number) {
      // This vuex prop is being watched by the task/convo detail header
      useAppStore().itemEventForLinkedTaskID = id
    },
    enableIncomingReplyEdit() {
      this.isEditing = true
    },
    disableEditMode() {
      this.isEditing = false
    },
    async submit() {
      this.isSubmitting = true

      try {
        await this.submitHandler(this.replyInfo.id, this.editedIncomingReply)
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isSubmitting = false
        this.disableEditMode()
      }
    },
  },
})
</script>

<style lang="scss">
.chronology-reply {
  position: relative;
  padding-left: 33px;

  @include media-breakpoint-up(lg) {
    padding-left: 0.5 * $detail-gutter-pad-lg;
  }

  &__reply-icon {
    &.ddi.ddi-return {
      position: absolute;
      font-size: 16px;
      color: var(--color-text-third);
      z-index: 4;
      top: 34px;
      left: 3px;

      @include media-breakpoint-up(md) {
        top: 10px;
        left: -22px;
      }

      @include media-breakpoint-up(lg) {
        font-size: 20px;
        top: 9px;
        left: -24px;
      }
    }
  }

  &__avatar {
    position: absolute;
    top: 6px;
    left: 0;

    @include media-breakpoint-up(lg) {
      top: 0;
      left: 6px;
    }
  }

  &__date {
    @include lettering--p-sm;
    color: var(--color-text-primary);
  }

  &__action-text {
    p {
      margin-bottom: 20px;
      word-break: break-word;
      @include lettering--p-md;

      a {
        @include lettering--normal-link;
      }
    }
  }

  &__other-info {
    margin-top: $detail-description-bottom-buffer;
  }

  &__more-options {
    position: absolute;
    top: -14px;
    right: 0;

    @include media-breakpoint-up(md) {
      // To handle collapsed history region
      z-index: 4;
      display: flex;
      align-items: center;
      top: -42px;
      right: 0;
      border: $border--solid-light;
      border-radius: 8px;
      padding: 4px 10px;
      background-color: var(--color-background-header);
      @include standard-box-shadow;

      .ddi {
        cursor: pointer;
        color: var(--color-text-third);
        padding: 4px 0;
      }

      .ddi-email-read,
      .ddi-edit {
        padding-right: 10px;
        border-right: $border--solid-light;
        margin-right: 10px;
      }
    }
  }

  &__form-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 10px;
  }
}
</style>
